import { practitionerIdSchema } from "@repo/api/schemas";
import type { PractitionerDto } from "@repo/data-store";
import { usePractitioners } from "@repo/data-store";
import { Button } from "@repo/ui/components/ui/button";
import { DialogFooter } from "@repo/ui/components/ui/dialog";
import { endOfDay, format, isToday, startOfDay } from "date-fns";
import * as React from "react";
import {
  CONSULTATION_TYPES,
  type ConsultBookingFormValues,
  useAppForm,
  useConsultBookingFormData,
} from "@/features/scheduling/forms/use-consult-booking-form";
import { focusFirstInvalid } from "@/shared/forms/focus-first-invalid";
import { patientDisplayName } from "@/shared/lib/patient-utils";
import { practitionerDisplayName } from "@/shared/lib/practitioner-utils";
import type { BookingPage } from "./booking-store";

interface ConsultFlowProps {
  page: BookingPage;
  onClose: () => void;
  onNext: () => void;
  onBack: () => void;
  initialPatientId?: string;
}

export function ConsultFlow({ page, onClose, onNext, onBack, initialPatientId }: ConsultFlowProps) {
  const {
    patients,
    isLoadingPatients,
    hsMap,
    isLoadingHs,
    isPending,
    defaultValues,
    validators,
    handleSubmit,
    slots,
    isLoadingAvailability,
    availabilityError,
    queryCandidates,
    resetCandidates,
  } = useConsultBookingFormData({ initialPatientId, onSuccess: onClose });

  const { practitioners, isLoading: isLoadingPractitioners } = usePractitioners({
    roles: ["clinician", "assistant"],
  });

  // The practitioner is named; nothing else about the calendars is. The server
  // resolves the practitioner's own schedule and, for in-person, every active
  // treatment room as the eligible set — so this component no longer looks up a
  // single schedule id, nor a location's.
  const lastFetchRef = React.useRef("");
  // Indirection so the form's onChange listener can call the query without the
  // form ↔ callback definition cycle. Assigned in an effect below.
  const queryTriggerRef = React.useRef<() => void>(() => {});

  const form = useAppForm({
    defaultValues: {
      ...defaultValues,
      patientId: initialPatientId ?? "",
    } as ConsultBookingFormValues,
    validators,
    onSubmitInvalid: focusFirstInvalid,
    listeners: {
      onChange: () => queryTriggerRef.current(),
    },
    onSubmit: async ({ value }) => {
      await handleSubmit(value);
    },
  });

  const runAvailabilityQuery = React.useCallback(() => {
    const { consultationType, practitionerId: pId, modality: mod, date } = form.state.values;
    if (!pId || !date) return;

    const hsId = hsMap[consultationType];
    if (!hsId || isLoadingHs) return;

    const key = `${consultationType}|${pId}|${mod}|${date.toISOString()}`;
    if (key === lastFetchRef.current) return;
    lastFetchRef.current = key;

    queryCandidates({
      healthcareServiceId: hsId,
      practitionerId: practitionerIdSchema.parse(pId),
      modality: mod,
      start: (isToday(date) ? new Date() : startOfDay(date)).toISOString(),
      end: endOfDay(date).toISOString(),
    });
  }, [form, hsMap, isLoadingHs, queryCandidates]);

  React.useEffect(() => {
    queryTriggerRef.current = runAvailabilityQuery;
    // Re-run when the async-resolved schedule ids change (captured in the callback's
    // identity) — covers in-person once its location schedule resolves.
    runAvailabilityQuery();
  }, [runAvailabilityQuery]);

  /** Any change to what is being asked for invalidates the slot already picked. */
  const clearPickedSlot = () => {
    resetCandidates();
    lastFetchRef.current = "";
    form.setFieldValue("startTime", "");
    form.setFieldValue("endTime", "");
  };

  // Confirm summary values
  const selectedStart = form.state.values.startTime;
  const selectedPatient = patients.find((p) => p.patientId === form.state.values.patientId);
  const selectedPractitioner: PractitionerDto | undefined = practitioners?.find(
    (p) => p.practitionerId === form.state.values.practitionerId,
  );

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        form.handleSubmit();
      }}
      className="space-y-4"
    >
      {/* Page 1: Form fields */}
      <div className={page !== "consult.form" ? "hidden" : undefined}>
        <div className="space-y-4">
          <form.AppField name="consultationType" listeners={{ onChange: clearPickedSlot }}>
            {(field) => (
              <field.OptionGroupField
                label="Type"
                options={CONSULTATION_TYPES.map((type) => ({ label: type.label, value: type.code }))}
              />
            )}
          </form.AppField>

          <form.AppField name="practitionerId" listeners={{ onChange: clearPickedSlot }}>
            {(field) => (
              <field.PractitionerField
                label="Practitioner"
                practitioners={practitioners ?? []}
                isLoading={isLoadingPractitioners}
              />
            )}
          </form.AppField>

          <form.AppField name="patientId">
            {(field) => (
              <field.PatientField
                label="Patient"
                patients={patients}
                isLoading={isLoadingPatients}
                disabled={!!initialPatientId}
              />
            )}
          </form.AppField>

          <form.AppField name="modality" listeners={{ onChange: clearPickedSlot }}>
            {(field) => (
              <field.OptionGroupField
                label="Modality"
                options={[
                  { label: "Virtual", value: "virtual" },
                  { label: "In-person", value: "in-person" },
                ]}
              />
            )}
          </form.AppField>

          <form.AppField name="date">{(field) => <field.DateField label="Date" fromDate={new Date()} />}</form.AppField>
        </div>
      </div>

      {/* Page 1 footer */}
      {page === "consult.form" && (
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onBack}>
            Back
          </Button>
          <form.Subscribe selector={(s) => !!(s.values.practitionerId && s.values.patientId && s.values.date)}>
            {(canAdvance) => (
              <Button type="button" disabled={!canAdvance} onClick={onNext}>
                Next
              </Button>
            )}
          </form.Subscribe>
        </DialogFooter>
      )}

      {/* Page 2: Slot picker */}
      <div className={page !== "consult.slots" ? "hidden" : undefined}>
        <div className="space-y-3">
          <form.AppField name="startTime">
            {(field) => (
              <field.AvailabilitySlotPicker
                label="Available Times"
                slots={slots}
                isLoading={isLoadingAvailability}
                error={availabilityError}
                onSlotSelect={(slot) => {
                  form.setFieldValue("endTime", slot.endTime);
                }}
              />
            )}
          </form.AppField>
        </div>
      </div>

      {/* Page 2 footer */}
      {page === "consult.slots" && (
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onBack} disabled={isPending}>
            Back
          </Button>
          <form.Subscribe selector={(s) => !!(s.values.startTime && s.values.endTime)}>
            {(hasSlot) => (
              <Button type="button" disabled={!hasSlot || isPending} onClick={onNext}>
                Next
              </Button>
            )}
          </form.Subscribe>
        </DialogFooter>
      )}

      {/* Page 3: Confirm */}
      <div className={page !== "consult.confirm" ? "hidden" : undefined}>
        <div className="space-y-3 rounded-lg border p-4">
          <div className="flex flex-col gap-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Type</span>
              <span className="font-medium">
                {CONSULTATION_TYPES.find((type) => type.code === form.state.values.consultationType)?.label} (60 min)
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Patient</span>
              <span className="font-medium">{patientDisplayName(selectedPatient) ?? "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Practitioner</span>
              <span className="font-medium">{practitionerDisplayName(selectedPractitioner) ?? "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Modality</span>
              <span className="font-medium capitalize">{form.state.values.modality}</span>
            </div>
            {selectedStart && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Time</span>
                <span className="font-medium">{format(new Date(selectedStart), "EEE, MMM d 'at' h:mm a")}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Page 3 footer */}
      {page === "consult.confirm" && (
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onBack} disabled={isPending}>
            Back
          </Button>
          <form.Subscribe selector={(s) => !!(s.values.startTime && s.values.endTime && s.values.patientId)}>
            {(canBook) => (
              <Button type="submit" disabled={!canBook || isPending}>
                {isPending ? "Booking..." : "Book Consultation"}
              </Button>
            )}
          </form.Subscribe>
        </DialogFooter>
      )}
    </form>
  );
}
